import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { QuickTapGrid } from '@/components/quick-tap/QuickTapGrid';
import { ShiftTotalBanner } from '@/components/quick-tap/ShiftTotalBanner';
import { UndoToast } from '@/components/quick-tap/UndoToast';
import { useActiveCnpjId, useExamTypes, useServices } from '@/features/services/hooks';
import {
  useActiveShifts,
  useShift,
  useShiftMutations,
  useShiftSummary,
} from '@/features/shifts/hooks';
import {
  itemCountersKey,
  useShiftItemMutations,
  type ItemCounters,
} from '@/features/production/hooks/useShiftItemMutations';
import { vibrate } from '@/lib/haptics';
import { ShiftTabs } from '../ShiftTabs';
import { ShiftSummaryPanel } from '../ShiftSummaryPanel';
import { ShiftEditor } from '../ShiftEditor';

export default function ShiftPage() {
  const { shiftId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { show } = useToast();
  const { cnpjId } = useActiveCnpjId();

  const { data: services = [] } = useServices(cnpjId);
  const { data: activeShifts = [] } = useActiveShifts(cnpjId);
  const shiftQuery = useShift(shiftId, cnpjId);
  const shift = shiftQuery.data?.shift;
  const totals = shiftQuery.data?.totals;
  const service = useMemo(
    () => services.find((s) => s.id === shift?.serviceId),
    [services, shift?.serviceId],
  );
  const { data: examTypes = [] } = useExamTypes(shift?.serviceId ?? null);
  const { data: summary } = useShiftSummary(
    shift?.status === 'DONE' ? shiftId : null,
  );

  const { start, end } = useShiftMutations(cnpjId);
  const unitValueOf = useMemo(() => {
    const map = new Map(examTypes.map((e) => [e.id, e.value]));
    return (id: string): number => map.get(id) ?? 0;
  }, [examTypes]);

  const { registerItem, undoLast, removeItem } = useShiftItemMutations(shiftId, unitValueOf);

  // Query "passiva" que apenas assina o cache de counters — escrever via
  // qc.setQueryData dispara re-render aqui.
  const countersQuery = useQuery<ItemCounters>({
    queryKey: itemCountersKey(shiftId),
    queryFn: () => ({}),
    enabled: !!shiftId,
    staleTime: Infinity,
    gcTime: Infinity,
    initialData: {},
  });
  const counters = countersQuery.data ?? {};

  // Rehidratar counters a partir do summary quando o plantão já tiver itens
  // no servidor (ex.: carregamento inicial, refetch após reopen).
  useEffect(() => {
    if (!shiftId) return;
    if (summary && shift?.status === 'DONE') {
      const next: ItemCounters = {};
      for (const i of summary.items) next[i.examTypeId] = i.qty;
      qc.setQueryData(itemCountersKey(shiftId), next);
    }
  }, [shiftId, summary, shift?.status, qc]);

  // Pilha local de toques para suportar undo correto no optimistic
  // (o servidor pode apagar um item diferente se houve concorrência,
  // mas na UX otimista o último é o mais recente).
  const tapStackRef = useRef<string[]>([]);
  const [undoKey, setUndoKey] = useState<string | null>(null);

  const handleTap = (examTypeId: string): void => {
    if (!shift || shift.status !== 'ACTIVE') return;
    vibrate(10);
    tapStackRef.current.push(examTypeId);
    setUndoKey(`${examTypeId}-${Date.now()}`);
    registerItem.mutate(
      { examTypeId },
      {
        onError: (e) => {
          tapStackRef.current.pop();
          setUndoKey(null);
          show({
            title: 'Não foi possível registrar',
            description: e instanceof Error ? e.message : '',
            variant: 'error',
          });
        },
      },
    );
  };

  const handleRemove = (examTypeId: string): void => {
    if (!shift || shift.status !== 'ACTIVE') return;
    vibrate(15);
    // Remove a última ocorrência deste exame da pilha de toques para manter o
    // undo do toast coerente com o que foi de fato retirado.
    const stack = tapStackRef.current;
    const idx = stack.lastIndexOf(examTypeId);
    if (idx !== -1) stack.splice(idx, 1);
    setUndoKey(null);
    removeItem.mutate(
      { examTypeId },
      {
        onError: (e) =>
          show({
            title: 'Não foi possível remover',
            description: e instanceof Error ? e.message : '',
            variant: 'error',
          }),
      },
    );
  };

  const handleUndo = (): void => {
    const last = tapStackRef.current.pop() ?? null;
    setUndoKey(null);
    vibrate(15);
    undoLast.mutate(
      { lastExamTypeId: last },
      {
        onError: (e) =>
          show({
            title: 'Erro ao desfazer',
            description: e instanceof Error ? e.message : '',
            variant: 'error',
          }),
      },
    );
  };

  if (shiftQuery.isLoading) {
    return <p className="p-4 text-sm text-(--color-fg-muted)">Carregando plantão…</p>;
  }
  if (!shift || !totals) {
    return (
      <div className="flex flex-col items-center gap-3 p-6">
        <p>Plantão não encontrado.</p>
        <Button onClick={() => navigate('/calendar')}>Voltar ao calendário</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <ShiftTabs activeShifts={activeShifts} services={services} />
      <ShiftTotalBanner
        fixedValue={totals.fixedValue}
        itemsTotal={totals.itemsTotal}
        total={totals.total}
        itemsCount={totals.itemsCount}
        serviceName={service?.name}
        serviceColor={service?.color}
      />

      <div className="flex flex-col gap-4 p-4">
        {shift.status === 'PENDING' && (
          <section className="rounded-md border border-(--color-border) bg-(--color-surface) p-4">
            <p className="text-sm">Plantão ainda não foi iniciado.</p>
            <Button
              className="mt-3"
              onClick={() =>
                start.mutate(shift.id, {
                  onError: (e) =>
                    show({
                      title: 'Erro',
                      description: e instanceof Error ? e.message : '',
                      variant: 'error',
                    }),
                })
              }
              disabled={start.isPending}
            >
              Iniciar plantão
            </Button>
          </section>
        )}

        {shift.status === 'ACTIVE' && (
          <>
            <QuickTapGrid
              examTypes={examTypes}
              counters={counters}
              onTap={handleTap}
              onRemove={handleRemove}
              disabled={registerItem.isPending && false}
            />
            <div className="flex justify-end">
              <Button
                variant="secondary"
                onClick={() => {
                  if (!confirm('Encerrar este plantão?')) return;
                  end.mutate(shift.id, {
                    onError: (e) =>
                      show({
                        title: 'Erro ao encerrar',
                        description: e instanceof Error ? e.message : '',
                        variant: 'error',
                      }),
                  });
                }}
                disabled={end.isPending}
              >
                Encerrar plantão
              </Button>
            </div>
          </>
        )}

        {shift.status === 'DONE' && summary && <ShiftSummaryPanel summary={summary} />}

        <ShiftEditor
          shift={shift}
          defaultFixedValue={service ? Number(service.fixedShiftValue) : 0}
          cnpjId={cnpjId}
        />
      </div>

      <UndoToast
        resetKey={undoKey}
        message="Procedimento registrado"
        durationMs={4000}
        onUndo={handleUndo}
        onExpire={() => {
          // Mantém a pilha do lado cliente; undo posterior não é oferecido
          // via toast após 4s (usuário pode usar endpoint de encerramento e
          // revisar via summary). Limpa para esconder UI.
          setUndoKey(null);
        }}
      />
    </div>
  );
}
